import { useEffect, useState } from "react";
import { Calendar, ChevronRight, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { supabase } from "../../../lib/supabase";

interface ScheduledMeeting {
  uuid: string;
  mentor: {
    name: string;
    email: string;
  };
  mentee: {
    name: string;
    email: string;
  };
  scheduled_time: string;
  duration: number;
  status: "scheduled" | "completed" | "cancelled";
  feedback?: any;
  meeting_link?: string;
  mentor_auth_token?: string;
  mentee_auth_token?: string;
}

export function ScheduledMeetingsList() {
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState<ScheduledMeeting[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchMeetings() {
      try {
        const { data, error: fetchError } = await supabase
          .from("meeting")
          .select(
            `
            uuid,
            scheduled_time,
            duration,
            status,
            feedback,
            meeting_link,
            mentor_auth_token,
            mentee_auth_token,
            mentor:mentor_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            ),
            mentee:mentee_uuid (
              uuid,
              vincer_base_user (
                name,
                email
              )
            )
          `
          )
          .order("scheduled_time", { ascending: false });

        if (fetchError) throw fetchError;

        if (data) {
          const formattedMeetings = (data as unknown as any[]).map(
            (meeting) => ({
              uuid: meeting.uuid,
              mentor: {
                name: meeting.mentor.vincer_base_user.name,
                email: meeting.mentor.vincer_base_user.email,
              },
              mentee: {
                name: meeting.mentee.vincer_base_user.name,
                email: meeting.mentee.vincer_base_user.email,
              },
              scheduled_time: meeting.scheduled_time,
              duration: meeting.duration,
              status: meeting.status,
              feedback: meeting.feedback,
              meeting_link: meeting.meeting_link,
              mentor_auth_token: meeting.mentor_auth_token,
              mentee_auth_token: meeting.mentee_auth_token,
            })
          );

          setMeetings(formattedMeetings);
        }
      } catch (err) {
        console.error("Error fetching meetings:", err);
        setError("Failed to load scheduled meetings");
      } finally {
        setLoading(false);
      }
    }

    fetchMeetings();
  }, []);

  const filteredMeetings = meetings.filter((meeting) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      meeting.mentor.name.toLowerCase().includes(searchLower) ||
      meeting.mentor.email.toLowerCase().includes(searchLower) ||
      meeting.mentee.name.toLowerCase().includes(searchLower) ||
      meeting.mentee.email.toLowerCase().includes(searchLower)
    );
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case "scheduled":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDateTime = (dateTimeString: string) => {
    const date = new Date(dateTimeString);
    return date.toLocaleString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-8 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Scheduled Meetings</h1>
        <button
          onClick={() => navigate("/coordinator/meetings/new")}
          className="flex items-center gap-2 rounded-lg bg-[#4CAF50] px-4 py-2 text-white transition-colors hover:bg-[#45a049]"
        >
          <Calendar className="size-5" />
          <span>Schedule Meeting</span>
        </button>
      </div>

      <div className="mb-6 rounded-xl border bg-white shadow-sm">
        <div className="border-b p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search meetings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border py-2 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#ffcc32]"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-b-2 border-[#ffcc32]"></div>
            <p className="text-gray-600">Loading scheduled meetings...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <p className="text-red-600">{error}</p>
          </div>
        ) : filteredMeetings.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-600">No scheduled meetings found</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredMeetings.map((meeting) => (
              <div
                key={meeting.uuid}
                className="cursor-pointer p-4 transition-colors hover:bg-gray-50"
                onClick={() =>
                  navigate(`/coordinator/meetings/${meeting.uuid}`)
                }
              >
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-3">
                      <h3 className="font-medium">{meeting.mentor.name}</h3>
                      <ChevronRight className="size-4 text-gray-400" />
                      <h3 className="font-medium">{meeting.mentee.name}</h3>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>{meeting.mentor.email}</span>
                      <span>→</span>
                      <span>{meeting.mentee.email}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatDateTime(meeting.scheduled_time)} (
                      {meeting.duration} minutes)
                    </div>
                    {meeting.feedback && (
                      <div className="text-sm italic text-gray-500">
                        Feedback available
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    <span
                      className={`rounded-full px-3 py-1 text-sm font-medium ${getStatusColor(meeting.status)}`}
                    >
                      {meeting.status.charAt(0).toUpperCase() +
                        meeting.status.slice(1)}
                    </span>
                    <ChevronRight className="size-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
